// Простой бот: случайные легальные ходы.
// Сделан отдельным модулем — в А.2+ заменим на умного.
//
// Б-2: бот учится играть Действия (всех 4 фракций) и Влияние Деуса.
// Принцип — random, без эвристик. Главное чтобы вообще играл.

import { selectAttackTarget } from '../engine/priorities.js';
import { getActivatableStealth, getStealthTargets, actionInitiationTurn } from '../engine/phases.js';
import { pickRandomGlobalPriority, needsAttackPriorityPicker, setAttackPriority } from '../engine/abilities/global/priority.js';
import { needsHealPriorityPicker, setHealPriority } from '../engine/abilities/global/heal-priority.js';
// Б-2: общие диспетчеры Действий (faction-aware) и Влияния Деуса.
import { actionNeedsTarget, getActionTargets } from '../engine/match.js';
import { canPlayInfluenceCard, playInfluenceCard, influenceNeedsTarget, STAND_ORDER } from '../engine/match.js';
// Б-4: статические эвристики выбора линии и слота.
import { chooseLine, chooseSlot } from './bot-placement-heuristic.js';
// Б-5: эвристики picker'ов цели/жертвы.
import {
  chooseEnemyByHighestAtk,
  chooseAllyByHighestAtk,
  chooseAllyByLowestHpRatio,
  chooseAllyByHighestAtkPreferDamaged,
  chooseEnemyByMostKeywords,
  chooseUnitByHighestAtkInDiscard,
  chooseAlliesToSacrificeByLowestHp
} from './bot-picker-heuristics.js';

// Б-2: id Действий со специальными opts (без opts эффект использует
// внутренний rng-фолбэк, но бот заполняет opts явно — это правильнее
// и совпадает с UI-флоу игрока).
const ID_PEREGRUPPIROVKA_GLB = 'glb_peregruppirovka';
const ID_PROPAGANDA_GLB      = 'glb_propaganda';

// Б-5: id Действий со «своя цель = свой юнит наивысшей атаки».
const ID_SINT_KOZHA_LIV    = 'liv_sint_kozha';
const ID_INEKTSIYA_LIV     = 'liv_inektsiya_adrenalina';
const ID_PERELIVANIE_LIV   = 'liv_perelivanie_krovi';
const ID_V_KARANTIN_LIV    = 'liv_v_karantin';
const ID_OTSIFROVKA_LIV    = 'liv_otsifrovka_dushi';
const ID_VIRUS_HV14_LIV    = 'liv_virus_hv_14';
const ID_VINTOVKA_GLB      = 'glb_shturmovaya_vintovka';
const ID_SCHIT_GLB         = 'glb_takticheskiy_schit';

// Б-6: мулиган. Сбрасываем дорогие карты (epic/legendary) — на ранних
// ходах они мертвы (initiation cooldown у юнитов, actionInitiationTurn
// сжигает Действия). Лимит 3 карты; legendary приоритетнее epic.
//
// Возвращает массив индексов в bot.hand (skvillBonus карты в applyMulligan
// фильтруются). common/rare и Моды — оставляем (работают с 1-го хода).
export function botMulligan(state, botIdx) {
  const bot = state.players[botIdx];
  if (!bot || !Array.isArray(bot.hand) || bot.hand.length === 0) return [];
  const candidates = [];
  for (let i = 0; i < bot.hand.length; i++) {
    const c = bot.hand[i];
    if (!c) continue;
    if (c.skvillBonus) continue;                 // applyMulligan и так фильтрует
    if (c.type === 'mod') continue;              // Моды бесплатны (через Сквилла) и работают сразу
    if (c.rarity === 'legendary' || c.rarity === 'epic') {
      candidates.push({ idx: i, rarity: c.rarity });
    }
  }
  // legendary > epic. Внутри одинаковых — порядок руки.
  candidates.sort((a, b) => {
    if (a.rarity === b.rarity) return a.idx - b.idx;
    return a.rarity === 'legendary' ? -1 : 1;
  });
  return candidates.slice(0, 3).map(c => c.idx);
}

// Решение по размещению (Фаза 2).
// Возвращает один из:
//   { kind: 'unit',   cardInstanceId, line, slot }
//   { kind: 'action', cardInstanceId, targetInstanceId, opts }
//   null
//
// Б-2: бот рассматривает в едином пуле юниты И Действия с равной вероятностью.
//   - Юниты — фильтр: есть свободный слот.
//   - Действия — фильтр: Initiation Cooldown (epic с 3-го хода, legendary с 4-го)
//     + наличие валидных целей (через getActionTargets).
//   - Random выбор одной карты, затем random цель/слот/opts.
//
// Бюджет (1 + extraPlaysNextTurn у Деуса; 1 у остальных) и Мод-слот Синтетика
// учитываются вызывающим кодом (см. canAddPlacement / getAvailablePlacements
// в match.js); botPlacement сам не считает бюджет — он просто отдаёт одно
// решение, и оркестратор останавливается, когда достиг лимита.
// Б-7-smart рефактор: разбито на helpers, чтобы умный бот (bot-smart.js)
// переопределял ТОЛЬКО выбор карты (pick), а сбор кандидатов и построение
// решения переиспользовались. Поведение рандомного botPlacement не изменилось.

// Собирает валидные кандидаты на размещение: [{kind:'unit'|'action', card}].
export function gatherPlacementCandidates(state, botIdx) {
  const bot = state.players[botIdx];
  if (!bot || bot.hand.length === 0) return [];
  const turn = state.currentTurn;
  const candidates = [];

  // Юниты — нужен свободный слот.
  if (bot.hasFreeSlot()) {
    for (const c of bot.hand) {
      if (c.type === 'unit') candidates.push({ kind: 'unit', card: c });
    }
  }

  // Действия — фильтр Initiation Cooldown + валидность целей. Также у бота
  // не должно быть активного pendingAction (одно Действие за ход — диздок 6.4).
  if (!bot.pendingAction) {
    for (const c of bot.hand) {
      if (c.type !== 'action') continue;
      if (turn < actionInitiationTurn(c.rarity)) continue;
      if (actionNeedsTarget(c.id)) {
        const targets = getActionTargets(state, botIdx, c.id);
        if (!targets || targets.length === 0) continue;
      }
      candidates.push({ kind: 'action', card: c });
    }
  }
  return candidates;
}

// Строит решение размещения из выбранного кандидата {kind, card}.
export function buildPlacementDecision(state, botIdx, pick, rng) {
  const bot = state.players[botIdx];
  const turn = state.currentTurn;
  const card = pick.card;

  if (pick.kind === 'unit') {
    // Б-4: эвристика — сначала линия (по свойствам карты), потом слот.
    let line = chooseLine(card, bot, turn);
    if (line === null) line = rng.int(2);
    // Страховка: если выбранная линия полна — на другую.
    let freeOnLine = 0;
    for (let s = 0; s < 4; s++) if (bot.field[line][s] === null) freeOnLine++;
    if (freeOnLine === 0) line = 1 - line;
    let slot = chooseSlot(card, line, bot, rng);
    if (slot === null) {
      const fb = bot.freeSlots();
      if (fb.length === 0) return null;
      line = fb[0].line;
      slot = fb[0].slot;
    }
    if (needsAttackPriorityPicker(card)) setAttackPriority(card, pickRandomGlobalPriority(rng));
    // Б-6: хил-приоритет фиксируем на lowest_hp — самый универсальный для медика.
    if (needsHealPriorityPicker(card))   setHealPriority(card,   'lowest_hp');
    const orderOpts = buildOrderOptsForUnit(state, botIdx, card, line, rng);
    return { kind: 'unit', cardInstanceId: card.instanceId, line, slot, orderOpts };
  }

  // Action — цель (если нужна) + opts.
  let targetInstanceId = null;
  if (actionNeedsTarget(card.id)) {
    const targets = getActionTargets(state, botIdx, card.id);
    if (!targets || targets.length === 0) return null;
    const picked = pickActionTarget(state, botIdx, card.id, targets, rng);
    targetInstanceId = picked?.instanceId ?? null;
  }
  const opts = buildActionOpts(state, botIdx, card, targetInstanceId, rng);
  return { kind: 'action', cardInstanceId: card.instanceId, targetInstanceId, opts };
}

export function botPlacement(state, botIdx, rng) {
  const candidates = gatherPlacementCandidates(state, botIdx);
  if (candidates.length === 0) return null;
  // Random равная вероятность среди всех валидных карт.
  const pick = candidates[rng.int(candidates.length)];
  return buildPlacementDecision(state, botIdx, pick, rng);
}

// Б-5: эвристика выбора цели для targeted-Действия. Если для этой карты
// эвристики нет — возвращаем random (как до Б-5).
function pickActionTarget(state, botIdx, cardId, targets, rng) {
  if (!targets || targets.length === 0) return null;
  const bot = state.players[botIdx];
  const enemyIdx = 1 - botIdx;
  const splitOwn = (arr) => arr.filter(u => u.ownerId === botIdx);
  const splitEnemy = (arr) => arr.filter(u => u.ownerId === enemyIdx);
  switch (cardId) {
    // Свой юнит наивысшей атаки.
    case ID_SINT_KOZHA_LIV:
    case ID_INEKTSIYA_LIV:
    case ID_PERELIVANIE_LIV:
    case ID_VINTOVKA_GLB:
    case ID_SCHIT_GLB:
      return chooseAllyByHighestAtk(splitOwn(targets), rng) || targets[rng.int(targets.length)];
    case ID_V_KARANTIN_LIV:
      return chooseAllyByHighestAtkPreferDamaged(splitOwn(targets), rng) || targets[rng.int(targets.length)];
    // Враг наивысшей атаки.
    case ID_VIRUS_HV14_LIV:
      return chooseEnemyByHighestAtk(splitEnemy(targets), rng) || targets[rng.int(targets.length)];
    case ID_OTSIFROVKA_LIV: {
      // Свой наивысшей атаки — иначе любой враг.
      const own = splitOwn(targets);
      if (own.length > 0) return chooseAllyByHighestAtk(own, rng) || own[rng.int(own.length)];
      const enemies = splitEnemy(targets);
      if (enemies.length > 0) return enemies[rng.int(enemies.length)];
      return targets[rng.int(targets.length)];
    }
    default:
      return targets[rng.int(targets.length)];
  }
}

// Б-5: pre-compute orderOpts для юнита-Order по эвристике. Возвращает
// объект opts или null (если у этой карты нет picker'а Order'а).
//
// Контракт: opts кладётся в state.pendingOrderOpts[botIdx][cardInstanceId];
// в Ф3 fireUnitOrderById передаёт opts в Order, тот предпочитает opts
// перед своим внутренним random-фолбэком.
function buildOrderOptsForUnit(state, botIdx, card, line, rng) {
  const bot = state.players[botIdx];
  const enemy = state.players[1 - botIdx];
  switch (card.id) {
    // БУГ.7: legacy glb_saper / glb_netranner_adyutant / glb_ofitser_svyazi
    // case'ы удалены. Эти ID не в v6 cards-global.js. Бот для нового
    // global_saper_onix получает orderOpts через `default → null`
    // (target picker не нужен боту, Order работает без target — engine
    // game-сторона использует pickGlobalTarget для armor_break).
    case 'liv_dron_defibrillyator': {
      // Свой юнит наивысшей атаки. Сам Дрон ещё в pendingPlacements,
      // фильтрация по instanceId страхует.
      const allies = bot.unitsOnField().filter(u =>
        u.type === 'unit' && u.instanceId !== card.instanceId
      );
      if (allies.length === 0) return null;
      const t = chooseAllyByHighestAtk(allies, rng);
      return t ? { immuneTargetInstanceId: t.instanceId } : null;
    }
    case 'liv_opytnyy_hirurg': {
      // Юнит из своего сброса наивысшей атаки.
      if (!bot.hasFreeSlot()) return null;
      const t = chooseUnitByHighestAtkInDiscard(bot.discard, rng);
      return t ? { resurrectInstanceId: t.instanceId } : null;
    }
    case 'liv_medik_shturmovik': {
      // Multi-select: до 2 раненых своих с минимальным current_hp;
      // если все полные — пустой список.
      const allies = bot.unitsOnField().filter(u =>
        u.type === 'unit' && u.instanceId !== card.instanceId
      );
      const sacrifices = chooseAlliesToSacrificeByLowestHp(allies, rng, 2);
      return { sacrificeInstanceIds: sacrifices.map(s => s.instanceId) };
    }
    case 'deus_auditor_banka': {
      // Order по линии: line 0 — возврат любого юнита (целим врага); line 1 — бафф своего.
      // Pre-fill оба ключа — Order прочитает соответствующий по своей линии.
      const opts = {};
      const enemies = enemy.unitsOnField().filter(u => u.type === 'unit');
      const enemyTop = chooseEnemyByHighestAtk(enemies, rng);
      if (enemyTop) opts.targetReturnInstanceId = enemyTop.instanceId;
      const allies = bot.unitsOnField().filter(u =>
        u.type === 'unit' && u.instanceId !== card.instanceId
      );
      const allyTop = chooseAllyByHighestAtk(allies, rng);
      if (allyTop) opts.targetAllyInstanceId = allyTop.instanceId;
      return Object.keys(opts).length > 0 ? opts : null;
    }
    default:
      return null;
  }
}

// Б-2: подготовка opts для Действий с двойным picker'ом / выбором линии.
//   - glb_peregruppirovka: opts = {line, slot} — random свободный слот
//     своей половины (исключая текущую ячейку выбранного юнита).
//   - glb_propaganda: opts = {line: 0|1} — random линия.
//   - Остальные Действия: opts = {} (4-й аргумент resolveAction игнорируется).
function buildActionOpts(state, botIdx, card, targetInstanceId, rng) {
  const bot = state.players[botIdx];
  if (card.id === ID_PEREGRUPPIROVKA_GLB) {
    // Найти выбранного юнита, чтобы исключить его текущую ячейку.
    let unit = null;
    if (targetInstanceId != null) {
      for (const u of bot.unitsOnField()) {
        if (u.instanceId === targetInstanceId) { unit = u; break; }
      }
    }
    const free = [];
    for (let l = 0; l < 2; l++) {
      for (let s = 0; s < 4; s++) {
        if (bot.field[l][s] !== null) continue;
        if (unit && unit.line === l && unit.slot === s) continue;
        free.push({ line: l, slot: s });
      }
    }
    if (free.length === 0) return {};
    const pick = free[rng.int(free.length)];
    return { line: pick.line, slot: pick.slot };
  }
  if (card.id === ID_PROPAGANDA_GLB) {
    return { line: rng.int(2) };
  }
  return {};
}

// Б-2: бот разыгрывает карту Влияния Деуса.
// Возвращает { cardId, targetCard? } или null.
//
//   - Только если фракция бота === 'deus'.
//   - 50% шанс на попытку (иначе пропуск, как у обычного игрока, который
//     не каждый ход тратит Влияние).
//   - Из STAND_ORDER берём те cardId, для которых canPlayInfluenceCard
//     вернул ok (по очкам Влияния, по слотам и т.д.).
//   - Кумовство — random союзный юнит на поле.
//   - Спящий агент / Парашют / Активный щит / Стимулятор / Набор —
//     без цели.
//
// Накопление очков — пассивкой Героя (Танаки/Кироса), бот их не трогает,
// только тратит.
//
// Этап 31-b: убран 50% шанс попытки. Диагностика показала что бот пропускал
// ~50% доступных карт Влияния, особенно у Сины/Кироса (Sina:48%, Kirosa:48%).
// Теперь:
//   1. Играем всегда когда есть доступная карта.
//   2. Приоритет: дорогая карта > дешёвая (тратим Влияние когда накопилось).
//   3. На раннем ходу (1-3) бережём Влияние — не тратим на cost ≤ 2 если
//      Влияние < 4 (хотим накопить на cost 3-5).
export function botInfluencePlay(state, botIdx, rng) {
  const bot = state.players[botIdx];
  if (bot.hero?.faction !== 'deus') return null;

  // Кандидаты с их стоимостью.
  const playable = [];
  for (const cardId of STAND_ORDER) {
    const can = canPlayInfluenceCard(state, botIdx, cardId);
    if (can?.ok) {
      // Узнаем cost через попытку (можно через специальный API но сейчас
      // выводим из влияния до/после). Здесь используем эвристику по id.
      const cost = INFLUENCE_COSTS[cardId] || 0;
      playable.push({ cardId, cost });
    }
  }
  if (playable.length === 0) return null;

  // Раннее накопление: если Влияние < 4 и ход ≤ 3, играем только дорогие
  // (cost ≥ 3). Иначе позволяем играть дешёвые.
  const turn = state.currentTurn || 0;
  const inf = bot.influence || 0;
  let pool = playable;
  if (turn <= 3 && inf < 4) {
    const dorogie = playable.filter(p => p.cost >= 3);
    if (dorogie.length > 0) pool = dorogie;
    // Если только дешёвые доступны на раннем ходу — играем их.
  }

  // Приоритет: дорогая карта (max cost) → тратим Влияние эффективно.
  pool.sort((a, b) => b.cost - a.cost);
  const top = pool[0];

  let targetCard = null;
  if (influenceNeedsTarget(top.cardId)) {
    const allies = bot.unitsOnField().filter(u => u.type === 'unit');
    if (allies.length === 0) return null;
    targetCard = allies[rng.int(allies.length)];
  }
  return { cardId: top.cardId, targetCard };
}

// Стоимости карт Влияния (Деус-стенд). Используем для приоритизации в боте.
// C.1 Этап 33: cost'ы синхронизированы с runtime COST в самих модулях карт.
const INFLUENCE_COSTS = {
  'deus_kumovstvo': 2,
  'deus_eksperimentalnyy_stimulyator': 4,
  'deus_nabor_v_korporatsiyu': 3,
  'deus_schit': 6,
  'deus_inf_spyashy_agent': 8,
  'deus_parashyut': 10
};

// Б.1.c: бот-Синтетикс надевает Мод. Возвращает {cardInstanceId, targetInstanceId} или null.
// Стратегия: случайный Мод из руки на случайного союзника (НЕ в Перегрузку).
//   - Если в руке только Моды (нет юнитов и Действий) — null (не сливаем HP даром).
//   - Цель: union(своих юнитов на поле, юнитов в pendingPlacements этого бота),
//     отфильтрованных условием modifications.length < overloadThreshold - 1.
//     Союзник в pendingPlacements ещё в руке, но появится на поле в той же Ф3.
//
// Б.2.c-fix2: Мод можно играть ТОЛЬКО если основная карта уже запланирована в этом
// ходу — иначе Мод бы был отброшен страховкой движка в Ф3. Проверка: pendingPlacements
// бота содержит юнит ИЛИ pendingAction != null.
export function botModPlay(state, botIdx, rng) {
  const bot = state.players[botIdx];
  if (bot.hero?.faction !== 'syntetics') return null;
  const mods = bot.hand.filter(c => c.type === 'mod');
  if (mods.length === 0) return null;
  // Б.2.c-fix2: основная карта должна быть В PENDING этого хода (не просто в руке).
  const placements = state.pendingPlacements?.[botIdx] || [];
  const pendingMain = placements.some(pl => {
    const c = bot.hand.find(h => h.instanceId === pl.cardInstanceId);
    return c?.type === 'unit' || c?.type === 'action';
  });
  if (!pendingMain && bot.pendingAction == null) return null;
  // Б-6: фатальный самоурон. Цена следующего Мода = min(modPlaysCount+1, 5).
  // Сквилл (syn_umnyak_skvill) и активный Инициатор отменяют урон, поэтому
  // для них проверка не применяется.
  const isSquill = bot.hero?.id === 'syn_skvill';
  const initiatorActive = !!(bot.activeInitiatorEffect && bot.activeInitiatorEffect.remainingTurns > 0);
  if (!isSquill && !initiatorActive) {
    const nextCost = Math.min((bot.modPlaysCount || 0) + 1, 5);
    if ((bot.hero?.current?.hp ?? 0) <= nextCost) return null;
  }
  // Кандидаты на цель: свои юниты на поле + только что размещённые в этой Ф2.
  // Б-6: жёстко исключаем юнитов с 2+ Модами (3-й Мод = Перегрузка).
  // Списанный дрон поднимает overloadThreshold до 4 — для него допустим
  // и 2-й Мод; min(2, threshold-1) даёт корректный лимит для обоих случаев.
  const candidates = [];
  for (const u of bot.unitsOnField()) {
    const limit = Math.min(2, (u.overloadThreshold ?? 3) - 1);
    if ((u.modifications?.length ?? 0) < limit) candidates.push(u);
  }
  // Также юниты, которые ботом запланированы в pendingPlacements этого хода.
  for (const pl of placements) {
    const c = bot.hand.find(h => h.instanceId === pl.cardInstanceId);
    if (c?.type !== 'unit') continue;
    const limit = Math.min(2, (c.overloadThreshold ?? 3) - 1);
    if ((c.modifications?.length ?? 0) < limit) candidates.push(c);
  }
  if (candidates.length === 0) return null;
  const mod = mods[rng.int(mods.length)];
  // Б.1.d / Б-6: эвристика выбора цели.
  //   1) Кибер-псих без Модов — бесплатный +1 atk от passive (приоритет).
  //   2) Иначе — наивысшая current.atk среди допустимых (стак на сильнейшего),
  //      тай-брейк через rng.
  const cyber = candidates.find(c => c.id === 'syn_kiberpsih' && (c.modifications?.length ?? 0) === 0);
  const target = cyber || pickHighestAtk(candidates, rng);
  return { cardInstanceId: mod.instanceId, targetInstanceId: target.instanceId };
}

// Б-6: вспомогательный — наивысшая current.atk с rng тай-брейком.
function pickHighestAtk(units, rng) {
  if (!units || units.length === 0) return null;
  let best = -Infinity;
  for (const u of units) {
    const a = u?.current?.atk ?? 0;
    if (a > best) best = a;
  }
  const tied = units.filter(u => (u?.current?.atk ?? 0) === best);
  if (tied.length === 1) return tied[0];
  return tied[rng.int(tied.length)];
}

// Решение по активации одного стелс-юнита бота (диздок 6.7, А.4.1-fix3).
// Возвращает { attacker, target } или null если активировать некого/некуда.
// Бот активирует только юниты, выставленные в ПРЕДЫДУЩИЕ ходы (см. getActivatableStealth).
// Цель — случайная из допустимых (с учётом Провокации).
export function botStealthActivation(state, botIdx, rng) {
  const eligible = getActivatableStealth(state, botIdx);
  if (eligible.length === 0) return null;
  // Берём первого подходящего (порядок: фронт → тыл, слева-направо).
  // Случайность тут не критична: главное — каждый ход выпускать своих.
  for (const attacker of eligible) {
    const t = getStealthTargets(state, botIdx, attacker);
    const candidates = [...t.units];
    if (t.heroAllowed) candidates.push(state.players[1 - botIdx].hero);
    if (candidates.length === 0) continue;
    const target = candidates[rng.int(candidates.length)];
    return { attacker, target };
  }
  return null;
}

// Решение по атаке Героем (Фазы 4/5).
// Возвращает Card-цель или null (пропустить).
// Использует общую selectAttackTarget — учитывает taunt и stealth противника.
export function botHeroAttack(state, botIdx, rng) {
  const bot = state.players[botIdx];
  const enemy = state.players[1 - botIdx];
  if (bot.hero.hp <= 0) return null;
  // Двойник Никоси: бонус-атака — если уже атаковал, но есть счётчик, можно ещё.
  if (bot.hero.heroAttackedThisTurn && (bot.heroBonusAttackPending || 0) <= 0) return null;

  const sel = selectAttackTarget(bot.hero, enemy, rng);
  if (sel.kind === 'skip') return null;
  return sel.target;
}

// А.4.5-fix2: бот за Сину иногда активирует peek (если ход >= 3 и не использовал).
// Решение принимает движок, бот информацию игнорирует.
// Возвращает true если активация запланирована.
export function botSinaActivation(state, botIdx, rng) {
  const bot = state.players[botIdx];
  if (bot.hero.id !== 'deus_sina') return false;
  if (bot.heroActiveUsed) return false;
  if (state.currentTurn < 3) return false;
  return rng.int(100) < 30;                  // 30% шанс
}

// А.4.5-fix3: бот за Кироса активирует захват ВСЕГДА когда доступно.
// Цель: первый legal target (1-я линия слева→направо, потом 2-я).
// Слот: первый свободный (1-я линия слева→направо, потом 2-я).
// Возвращает {targetCard, slot:{line,slot}} или null.
export function botKirosaActivation(state, botIdx /* rng не нужен — детерминированно */) {
  const bot = state.players[botIdx];
  if (bot.hero.id !== 'deus_kirosa') return null;
  if (bot.heroActiveUsed) return null;
  if (!bot.hasFreeSlot()) return null;
  const enemy = state.players[1 - botIdx];
  // Первая legal цель в порядке: enemy[0][0..3], затем enemy[1][0..3].
  let target = null;
  for (let line = 0; line < 2 && !target; line++) {
    for (let slot = 0; slot < 4 && !target; slot++) {
      const u = enemy.field[line][slot];
      if (!u) continue;
      if (u.type !== 'unit' || !u.isAlive()) continue;
      if (u.atk > 3) continue;
      if (u.sleeperState != null) continue;
      if (Array.isArray(u.keywords) && u.keywords.includes('stealth')) continue;
      target = u;
    }
  }
  if (!target) return null;
  let slotPick = null;
  for (let line = 0; line < 2 && !slotPick; line++) {
    for (let slot = 0; slot < 4 && !slotPick; slot++) {
      if (bot.field[line][slot] === null) slotPick = { line, slot };
    }
  }
  if (!slotPick) return null;
  return { targetCard: target, slot: slotPick };
}

// Б-2: helper для оркестратора (main.js / smoke-test / test-harness).
// Применяет решение botPlacement к state. Юнит → setPendingPlacement;
// Действие → p.pendingAction. Возвращает true, если применилось.
//
// setPendingPlacementFn передаётся внешне, чтобы избежать кругового импорта
// (этот модуль не зависит от match.js на уровне placement-API).
export function applyBotPlacementDecision(state, botIdx, decision, setPendingPlacementFn) {
  if (!decision) return false;
  if (decision.kind === 'action') {
    state.players[botIdx].pendingAction = {
      cardInstanceId: decision.cardInstanceId,
      targetInstanceId: decision.targetInstanceId ?? null,
      opts: decision.opts || {}
    };
    return true;
  }
  // 'unit' (или старый формат без kind — на случай legacy-вызова).
  setPendingPlacementFn(state, botIdx, {
    cardInstanceId: decision.cardInstanceId,
    line: decision.line,
    slot: decision.slot
  });
  // Б-5: pre-filled orderOpts для Order'а юнита. В Ф3 fireUnitOrderById
  // прочитает их из state.pendingOrderOpts[botIdx][cardInstanceId].
  if (decision.orderOpts) {
    if (!state.pendingOrderOpts) state.pendingOrderOpts = [{}, {}];
    if (!state.pendingOrderOpts[botIdx]) state.pendingOrderOpts[botIdx] = {};
    state.pendingOrderOpts[botIdx][decision.cardInstanceId] = decision.orderOpts;
  }
  return true;
}
